import { useState, type MouseEvent, type ComponentProps } from "react";
import { useLogin, useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

/**
 * Sign in through a GoTrue external OAuth/OIDC provider.
 *
 * Deliberately a separate file from the upstream `SSOAuthButton` (which is
 * SAML-only) so upstream merges stay conflict-free.
 */
export const OIDCAuthButton = ({
  children,
  provider,
  redirect: redirectTo,
  ...props
}: OIDCAuthButtonProps) => {
  const login = useLogin();
  const notify = useNotify();
  const [isPending, setIsPending] = useState(false);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsPending(true);
    login(
      { oauthProvider: provider },
      redirectTo ?? window.location.toString(),
    ).catch((error) => {
      setIsPending(false);
      // The authProvider always rejects for OAuth login, but with no error
      // when the call actually succeeded — that keeps react-admin from
      // redirecting before the browser reaches the provider.
      if (error) {
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          { type: "error" },
        );
      }
    });
  };

  return (
    <Button type="button" onClick={handleClick} disabled={isPending} {...props}>
      {children}
      {isPending ? (
        <Spinner
          className="text-primary-foreground size-4"
          data-icon="inline-start"
        />
      ) : null}
    </Button>
  );
};

export type OIDCAuthButtonProps = {
  provider: string;
  redirect?: string;
} & ComponentProps<typeof Button>;
